import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

// Constants
const MAX_UPLOAD_COUNT = 10;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

// Valid file types
const VALID_IMAGE_TYPES = /image\/(jpeg|png|gif|webp)/;
const VALID_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
const VALID_DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'doc', 'txt', 'csv', 'xlsx', 'xls', 'pptx', 'ppt'];
const VALID_IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp';

/**
 * Check if file is a valid image
 * @param {File} file - File to check
 * @returns {boolean}
 */
const isValidImageFile = (file) => {
    if (!file) return false;
    // Check by mime type first (more accurate for images)
    if (VALID_IMAGE_TYPES.test(file.type)) return true;
    // Fallback to extension
    const ext = file.name?.split('.').pop()?.toLowerCase();
    return VALID_IMAGE_EXTENSIONS.includes(ext);
};

/**
 * Check if file is a valid document
 * @param {File} file - File to check
 * @returns {boolean}
 */
const isValidDocumentFile = (file) => {
    if (!file?.name) return false;
    const ext = file.name.split('.').pop()?.toLowerCase();
    return VALID_DOCUMENT_EXTENSIONS.includes(ext);
};

/**
 * Check if file type is valid (image or document)
 * @param {File} file - File to check
 * @returns {boolean}
 */
const isValidFileType = (file) => {
    return isValidImageFile(file) || isValidDocumentFile(file);
};

/**
 * Validate files for image upload only (strict image type check)
 * @param {Object} params - Validation parameters
 * @param {File[]} params.newFiles - New files to upload
 * @param {Object[]} params.currentUploadImgs - Current uploaded images
 * @param {Object[]} params.currentUploadFiles - Current uploaded files
 * @param {Function} params.enqueueSnackbar - Snackbar function
 * @returns {{ isValid: boolean, imageFiles: File[] }}
 */
const validateImagesOnly = ({
    newFiles = [],
    currentUploadImgs = [],
    currentUploadFiles = [],
    enqueueSnackbar
}) => {
    const result = {
        isValid: true,
        imageFiles: []
    };

    if (!newFiles || newFiles.length === 0) {
        return result;
    }

    // Check if any file is not a valid image
    const invalidFiles = newFiles.filter(file => !isValidImageFile(file));
    if (invalidFiles.length > 0) {
        enqueueSnackbar?.(buildSnackbarMessage('INVALID_AI_SUPPORT_IMAGE_TYPE'), {
            variant: 'error',
            autoHideDuration: 2000
        });
        result.isValid = false;
        return result;
    }

    // All files are valid images
    result.imageFiles = [...newFiles];

    // Check total count (current + new)
    const currentTotalCount = currentUploadImgs.length + currentUploadFiles.length;
    const newTotalCount = currentTotalCount + result.imageFiles.length;

    if (newTotalCount > MAX_UPLOAD_COUNT) {
        enqueueSnackbar?.(buildSnackbarMessage('INVALID_TOO_MANY_IMAGES'), {
            variant: 'error',
            autoHideDuration: 2000
        });
        result.isValid = false;
        return result;
    }

    // Check each file size
    for (const file of result.imageFiles) {
        if (file.size > MAX_FILE_SIZE) {
            enqueueSnackbar?.(buildSnackbarMessage('INVALID_TOO_LARGE_IMAGE'), {
                variant: 'error',
                autoHideDuration: 2000
            });
            result.isValid = false;
            return result;
        }
    }

    return result;
};

/**
 * Validate files for document upload only (strict document type check)
 * @param {Object} params - Validation parameters
 * @param {File[]} params.newFiles - New files to upload
 * @param {Object[]} params.currentUploadImgs - Current uploaded images
 * @param {Object[]} params.currentUploadFiles - Current uploaded files
 * @param {Function} params.enqueueSnackbar - Snackbar function
 * @returns {{ isValid: boolean, documentFiles: File[] }}
 */
const validateDocumentsOnly = ({
    newFiles = [],
    currentUploadImgs = [],
    currentUploadFiles = [],
    enqueueSnackbar
}) => {
    const result = {
        isValid: true,
        documentFiles: []
    };

    if (!newFiles || newFiles.length === 0) {
        return result;
    }

    // Check if any file is not a valid document
    const invalidFiles = newFiles.filter(file => !isValidDocumentFile(file));
    if (invalidFiles.length > 0) {
        enqueueSnackbar?.(buildSnackbarMessage('INVALID_SEND_AI_SUPPORT_DOCUMENT_TYPE'), {
            variant: 'error',
            autoHideDuration: 2000
        });
        result.isValid = false;
        return result;
    }

    // All files are valid documents
    result.documentFiles = [...newFiles];

    // Check total count (current + new)
    const currentTotalCount = currentUploadImgs.length + currentUploadFiles.length;
    const newTotalCount = currentTotalCount + result.documentFiles.length;

    if (newTotalCount > MAX_UPLOAD_COUNT) {
        enqueueSnackbar?.(buildSnackbarMessage('INVALID_SEND_AI_TOO_MANY_FILE'), {
            variant: 'error',
            autoHideDuration: 2000
        });
        result.isValid = false;
        return result;
    }

    // Check each file size
    for (const file of result.documentFiles) {
        if (file.size > MAX_FILE_SIZE) {
            enqueueSnackbar?.(buildSnackbarMessage('INVALID_SEND_AI_TOO_LARGE_FILE'), {
                variant: 'error',
                autoHideDuration: 2000
            });
            result.isValid = false;
            return result;
        }
    }

    return result;
};

/**
 * Validate files for AI Chat upload
 * @param {Object} params - Validation parameters
 * @param {File[]} params.newFiles - New files to upload
 * @param {Object[]} params.currentUploadImgs - Current uploaded images
 * @param {Object[]} params.currentUploadFiles - Current uploaded files
 * @param {boolean} params.allowDocuments - Whether document files are allowed (default: true)
 * @param {Function} params.enqueueSnackbar - Snackbar function
 * @returns {{ isValid: boolean, validFiles: File[], imageFiles: File[], documentFiles: File[] }}
 */
const validateUploadForAiChat = ({
    newFiles = [],
    currentUploadImgs = [],
    currentUploadFiles = [],
    allowDocuments = true,
    enqueueSnackbar
}) => {
    const result = {
        isValid: true,
        validFiles: [],
        imageFiles: [],
        documentFiles: []
    };

    if (!newFiles || newFiles.length === 0) {
        return result;
    }

    // Filter and categorize valid files
    for (const file of newFiles) {
        if (isValidImageFile(file)) {
            result.imageFiles.push(file);
            result.validFiles.push(file);
        } else if (isValidDocumentFile(file)) {
            result.documentFiles.push(file);
            result.validFiles.push(file);
        }
        // Invalid file types are silently ignored (not added to validFiles)
    }

    // If documents are not allowed, silently remove document files from results
    if (!allowDocuments && result.documentFiles.length > 0) {
        result.documentFiles = [];
        result.validFiles = result.validFiles.filter(file => !isValidDocumentFile(file));

        // If no valid files remain after filtering, reject
        if (result.validFiles.length === 0) {
            result.isValid = false;
            return result;
        }
    }

    // If no valid files after filtering, check if there were files to begin with
    if (result.validFiles.length === 0 && newFiles.length > 0) {
        // All files were invalid type
        enqueueSnackbar?.(buildSnackbarMessage('INVALID_SEND_AI_SUPPORT_FILE_TYPE'), {
            variant: 'error',
            autoHideDuration: 2000
        });
        result.isValid = false;
        return result;
    }

    // Check total count (current + new)
    const currentTotalCount = currentUploadImgs.length + currentUploadFiles.length;
    const newTotalCount = currentTotalCount + result.validFiles.length;

    if (newTotalCount > MAX_UPLOAD_COUNT) {
        enqueueSnackbar?.(buildSnackbarMessage('INVALID_SEND_AI_TOO_MANY_FILE'), {
            variant: 'error',
            autoHideDuration: 2000
        });
        result.isValid = false;
        return result;
    }

    // Check each file size
    for (const file of result.validFiles) {
        if (file.size > MAX_FILE_SIZE) {
            enqueueSnackbar?.(buildSnackbarMessage('INVALID_SEND_AI_TOO_LARGE_FILE'), {
                variant: 'error',
                autoHideDuration: 2000
            });
            result.isValid = false;
            return result;
        }
    }

    return result;
};

export {
    MAX_UPLOAD_COUNT,
    MAX_FILE_SIZE,
    VALID_IMAGE_TYPES,
    VALID_IMAGE_EXTENSIONS,
    VALID_DOCUMENT_EXTENSIONS,
    VALID_IMAGE_ACCEPT,
    isValidImageFile,
    isValidDocumentFile,
    isValidFileType,
    validateUploadForAiChat,
    validateImagesOnly,
    validateDocumentsOnly
};
